import { symRoundedList } from '@quasar/extras/material-symbols-rounded'
import destr from 'destr'
import { useCrudManagerStore } from 'src/CrudManager/Core/stores/useCrudManagerStore'
import type { CrudInstance, CrudObject } from 'src/CrudManager/Core/types/AdvancedCrudManager'
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { defineAsyncComponent } from 'vue'
import type { CreatorInstanceData, CreatorModelConfig } from './types'

/**
 * Boots a creator instance by initializing default data and merging with query parameters
 * @param instance - The CRUD instance to boot
 * @returns The configured instance
 */
export function bootCreatorInstance(instance: CrudInstance): CrudInstance {
  // Attempt to get props, falling back to empty object
  const instanceProps = (instance.props || {}) as Partial<CreatorModelConfig>

  // Ensure instance.data exists with proper structure
  if (!instance.data) {
    instance.data = {
      facetDistribution: {},
      item: { id: crypto.randomUUID() },
    } as CreatorInstanceData
  }

  // Cast to CreatorInstanceData for proper type checking
  const creatorData = instance.data as CreatorInstanceData

  if (!creatorData.item) {
    creatorData.item = { id: crypto.randomUUID() } // Initialize with UUID if item doesn't exist
  }

  // Resolve defaults + URL query params once; both branches need them.
  let defaultData: Partial<CrudObject> = {}
  if (instanceProps?.defaultItemData) {
    defaultData =
      typeof instanceProps.defaultItemData === 'function'
        ? instanceProps.defaultItemData({} as CrudObject)
        : destr(JSON.stringify(instanceProps.defaultItemData))
  }
  if (typeof defaultData !== 'object' || defaultData === null) {
    defaultData = {}
  }

  const urlParams = new URLSearchParams(window.location.search)
  const rawQueryParams: Record<string, string> = {}
  urlParams.forEach((value, key) => {
    rawQueryParams[key] = value
  })

  // First boot leaves originalItem empty (createInstance seeds it as {}).
  // Restored instances carry the snapshot taken at the end of their first boot.
  // Invariant: originalItem is always a non-empty deep-clone of item after first boot,
  // so an empty object reliably signals a fresh instance. Do not reset originalItem to {}
  // after first boot (e.g., during store migrations) or this heuristic breaks.
  const persistedOriginal = (creatorData.originalItem ?? {}) as Record<string, unknown>
  const isFirstBoot = Object.keys(persistedOriginal).length === 0

  if (isFirstBoot) {
    // Menu / duplicate prefill from props.data wins over default empty fields
    // (name: '', etc.). Applied once, then frozen into originalItem so later
    // boots can't re-spread it over user edits.
    const menuData = (instance.props?.data as Record<string, unknown>) || {}
    creatorData.item = {
      ...defaultData,
      ...creatorData.item,
      ...rawQueryParams,
      ...menuData,
      id: (menuData.id as string | undefined) || creatorData.item.id || crypto.randomUUID(),
    }
    creatorData.originalItem = destr(JSON.stringify(creatorData.item))
  } else {
    // Re-hydration of a restored instance. Preserve the user's persisted item
    // verbatim; only fill in fields that don't exist yet (e.g. defaults added
    // since the instance was first persisted).
    const mergedItem: Record<string, unknown> = { ...creatorData.item }
    const defaultDict = defaultData as Record<string, unknown>
    Object.keys(defaultDict).forEach((key) => {
      if (mergedItem[key] === undefined) {
        mergedItem[key] = defaultDict[key]
      }
    })
    Object.keys(rawQueryParams).forEach((key) => {
      if (mergedItem[key] === undefined) {
        mergedItem[key] = rawQueryParams[key]
      }
    })
    creatorData.item = {
      ...mergedItem,
      id: (creatorData.item.id as string | undefined) || crypto.randomUUID(),
    }
  }

  instance.status.disableSaveWatcher = true

  // skipCreation is a first-boot-only signal: fire-and-forget save immediately
  // without waiting for user interaction. Guard with isFirstBoot so that
  // bootRestoredInstances re-hydrating a persisted creator doesn't trigger a
  // duplicate POST on every page reload.
  if (isFirstBoot && (creatorData.item as CrudObject & { skipCreation?: boolean }).skipCreation) {
    saveCreatorInstance(instance)
  }

  return instance
}

/**
 * Saves a creator instance by posting data to the API and optionally navigating to editor
 * @param instance - The CRUD instance to save
 * @returns Promise that resolves on success or returns error on failure
 * @throws {Error} When API request fails
 */
async function saveCreatorInstance(instance: CrudInstance): Promise<void | Error> {
  const instanceData = instance.data as CreatorInstanceData
  const crudManagerStore = useCrudManagerStore()

  if (!instanceData.item.id) {
    // Assign instance id if item id doesn't exist (ensure string type)
    instanceData.item.id = String(instance.id || crypto.randomUUID())
  }

  try {
    const model = await crudManagerStore.getFullModel(instance.modelName)
    if (!model) {
      console.error(`Could not load model: ${instance.modelName}`)
      return
    }

    // Use new RESTful API pattern: POST /{model}
    const { data } = await axiosApi.post(`cm/${model.apiUrl}`, {
      data: instanceData.item,
    })

    const m = crudManagerStore.getModule('editor')
    if (!m) return

    if (!instance.status.useDialog) {
      await crudManagerStore.removeInstance(instance, false)
      crudManagerStore.createInstance(model.name, 'editor', { modelId: data.data.id }, instance.parentId)
    }
  } catch (error) {
    return error as Error
  }
}

export default {
  name: 'creator',
  slugs: ['create', 'creer'],
  icon: symRoundedList,
  titleIsPlural: false,
  mainComponent: defineAsyncComponent(() => import('./MainComponent.vue')),
  boot: bootCreatorInstance,
  save: saveCreatorInstance,
}
